import { useState } from "react";
import { Button } from "@/components/ui/button";
import AuthModal from "@/components/AuthModal";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuthState } from "@/hooks/useAuthState";
import { startProCheckout } from "@/lib/proCheckout";
import { toast } from "sonner";
import { Lock } from "lucide-react";

interface GatedContentProps {
  children: React.ReactNode;
  /** Acceso completo: solo Club Pro (o administrador). */
  hasClubPro: boolean;
}

/**
 * Artículos Club Pro: la página es pública y el primer tercio del texto se lee en
 * abierto; a partir de ahí se difumina y aparece la invitación al Club Pro.
 * El contenido completo queda en el DOM solo para quien tiene Club Pro.
 */
const GatedContent = ({ children, hasClubPro }: GatedContentProps) => {
  const [authOpen, setAuthOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { lang, t } = useLanguage();
  const { isAuthenticated } = useAuthState();

  if (hasClubPro) {
    return <>{children}</>;
  }

  const handleJoin = async () => {
    setBusy(true);
    const result = await startProCheckout({ isAuthenticated, lang });
    setBusy(false);
    if (result === "needs-auth") {
      setAuthOpen(true);
      return;
    }
    if (result === "error") {
      toast.error(
        lang === "es"
          ? "No hemos podido abrir el pago. Inténtalo de nuevo."
          : "We couldn't open the checkout. Please try again.",
      );
    }
  };

  return (
    <div className="relative">
      {/* Se lee aproximadamente el primer tercio; el resto se difumina. */}
      <div className="relative max-h-[38vh] min-h-[320px] overflow-hidden">
        {children}

        <div
          className="absolute bottom-0 left-0 right-0 h-64 pointer-events-none"
          style={{
            background: "linear-gradient(to bottom, transparent 0%, hsl(var(--background)) 88%)",
          }}
        />
      </div>

      {/* Invitación al Club Pro */}
      <div className="relative -mt-24 z-10 flex justify-center px-4">
        <div className="w-full max-w-md bg-card rounded-2xl border border-border shadow-2xl p-8 text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-11 h-11 rounded-full bg-muted border border-border flex items-center justify-center">
              <Lock className="w-5 h-5 text-foreground" strokeWidth={1.5} />
            </div>
          </div>
          <h3 className="whitespace-pre-line font-display text-xl font-bold text-foreground">
            {t("insightsPaywall", "notice")}
          </h3>
          <p className="font-body text-sm text-muted-foreground leading-relaxed">
            {t("insightsPaywall", "reinforce")}
          </p>
          <Button
            size="lg"
            className="w-full rounded-xl text-base font-bold whitespace-normal h-auto min-h-11 py-2.5"
            disabled={busy}
            onClick={handleJoin}
          >
            {t("insightsPaywall", "cta")}
          </Button>
        </div>
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
};

export default GatedContent;
